// src/pages/Reports/BillWiseProfitLoss/components/exportBillWiseProfitLossCsv.js
import dayjs from "dayjs";
import _ from "lodash";

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const fmtNum  = (val) => Number(val || 0).toFixed(2);
const fmtDate = (val) => (val ? dayjs(val).format("DD-MMM-YYYY") : "-");

/**
 * @param {object} params
 * @param {Array}  params.data           - bill-wise grouped data (state.billWiseProfitLoss.data)
 * @param {Array}  params.productSummary - product summary data
 * @param {object} params.totals         - { totalSold, totalPurchased, totalProfit }
 * @param {object} params.filters        - { filterType, fromDate, toDate }
 * @param {string} params.title
 * @param {string} params.filename
 */
export const exportBillWiseProfitLossCsv = ({
  data,
  productSummary,
  totals,
  filters,
  title    = "Bill Wise Profit & Loss Report",
  filename = "bill_wise_profit_loss_report",
}) => {
  const rows = [
    [title],
    [`From: ${fmtDate(filters.fromDate)}  To: ${fmtDate(filters.toDate)}`],
    [],
  ];

  if (filters.filterType === "product_summary") {
    // ── Product Summary mode ─────────────────────────────────
    const headers = [
      "Product Name", "Product Code",
      "Sold Qty", "Sold Amount", "Purchased Amount", "Profit Amount",
    ].map(escape);

    rows.push(headers);

    productSummary.forEach((row) => {
      rows.push([
        row.prod_name             || "",
        row.prod_code             || "",
        row.prod_sold_qty         ?? 0,
        fmtNum(row.prod_sold_amount),
        fmtNum(row.prod_purchased_amount),
        fmtNum(row.prod_profit_amount),
      ].map(escape));
    });

    const totalSold      = _.sumBy(productSummary, (r) => Number(r.prod_sold_amount)      || 0);
    const totalPurchased = _.sumBy(productSummary, (r) => Number(r.prod_purchased_amount) || 0);
    const totalProfit    = _.sumBy(productSummary, (r) => Number(r.prod_profit_amount)    || 0);

    rows.push([]);
    rows.push([
      "Total", "", "",
      fmtNum(totalSold),
      fmtNum(totalPurchased),
      fmtNum(totalProfit),
    ].map(escape));

  } else {
    // ── Bill-wise (grouped) mode ─────────────────────────────
    const headers = [
      "Invoice No", "Date", "Customer",
      "Code", "Product Name",
      "Sold Rate", "Sold Qty", "Sold",
      "Purchase Rate", "Purchased", "Profit/Loss",
    ].map(escape);

    rows.push(headers);

    data.forEach((sale) => {
      const subSold      = _.sumBy(sale.details, (d) => Number(d.netSoldAmount      || 0));
      const subPurchased = _.sumBy(sale.details, (d) => Number(d.purchasedAmount    || 0));
      const subProfit    = _.sumBy(sale.details, (d) => Number(d.productProfitLoss  || 0));

      // Invoice header row
      rows.push([
        sale.sale_invoice  || "",
        dayjs(sale.sale_created_isodt).format("DD MMM YYYY hh:mm A"),
        `${sale.customer_name || ""} (${sale.customer_code || ""})`,
        "", "", "", "", "", "", "", "",
      ].map(escape));

      // Detail rows — invoice columns left blank
      sale.details.forEach((d) => {
        rows.push([
          "", "", "",
          d.item_code             || "",
          d.item_name             || "",
          fmtNum(d.sale_rate),
          d.sale_qty              ?? 0,
          fmtNum(d.netSoldAmount),
          fmtNum(d.purchase_average_rate),
          fmtNum(d.purchasedAmount),
          fmtNum(d.productProfitLoss),
        ].map(escape));
      });

      // Sub-total row
      rows.push([
        "", "", "", "", "", "", "",
        `Sub Total Sold: ${fmtNum(subSold)}`,
        "",
        `Sub Total Purchased: ${fmtNum(subPurchased)}`,
        `Sub Total Profit: ${fmtNum(subProfit)}`,
      ].map(escape));

      rows.push(Array(11).fill("").map(escape)); // blank spacer between invoices
    });

    // Grand total
    rows.push([]);
    rows.push([
      "Grand Total", "", "", "", "", "", "",
      fmtNum(totals.totalSold),
      "",
      fmtNum(totals.totalPurchased),
      fmtNum(totals.totalProfit),
    ].map(escape));
  }

  const csv  = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};